import { log } from "@be-miniapp/logging"
import type { Location } from "@be-miniapp/types"

import { useUserPersistingData } from "@/store/user-persisting-data"

const DEFAULT_LOCATION_TIMEOUT_MS = 10000

const options: PositionOptions = {
  enableHighAccuracy: true,
  maximumAge: 0,
}

interface GetLocationAsyncProps {
  onTimeout?: () => void
  providedLocation?: Location | null
  timeoutMs?: number
}

interface LocationPositionResult {
  coords: {
    latitude: number
    longitude: number
  }
}

function hasValidLocation(location?: Location | null): location is Location {
  return (
    Number.isFinite(location?.latitude) && Number.isFinite(location?.longitude)
  )
}

export const getLocationAsync = async ({
  onTimeout,
  providedLocation,
  timeoutMs = DEFAULT_LOCATION_TIMEOUT_MS,
}: GetLocationAsyncProps = {}): Promise<LocationPositionResult> => {
  // Priority 1: Use provided location from URL params if available
  if (hasValidLocation(providedLocation)) {
    log({
      category: "location-permission",
      level: "info",
      message: "Using location provided via URL parameters",
      data: { location: providedLocation },
    })

    return Promise.resolve({
      coords: {
        latitude: providedLocation.latitude,
        longitude: providedLocation.longitude,
      },
    })
  }

  // Priority 2: Fall back to geolocation API if no provided location
  if (!navigator?.geolocation?.getCurrentPosition) {
    throw {
      code: 4,
      message: "Geolocation is not supported by this browser",
    }
  }

  let hasUserResponded = false
  const locationTimeoutMs = Math.max(timeoutMs, 1000)

  return new Promise((resolve, reject) => {
    let timeoutId: number | undefined

    const onResolved = (position: LocationPositionResult) => {
      if (hasUserResponded) return
      hasUserResponded = true
      if (timeoutId) {
        window.clearTimeout(timeoutId)
      }
      resolve(position)
    }

    const onReject = (err: unknown) => {
      if (hasUserResponded) return
      hasUserResponded = true
      if (timeoutId) {
        window.clearTimeout(timeoutId)
      }
      reject(err)
      log({
        category: "location-permission",
        level: "error",
        message: "Failed to get location",
        data: err,
      })
    }

    navigator.geolocation.getCurrentPosition(onResolved, onReject, {
      ...options,
      timeout: locationTimeoutMs,
    })

    timeoutId = window.setTimeout(() => {
      const persistedLocation = useUserPersistingData.getState().location

      if (hasUserResponded) {
        return
      }

      if (hasValidLocation(persistedLocation)) {
        onResolved({
          coords: {
            latitude: persistedLocation.latitude,
            longitude: persistedLocation.longitude,
          },
        })

        return
      }

      if (!hasUserResponded) {
        onTimeout?.()
        onReject({
          code: "LOCATION_TIMEOUT",
          message: "Location request timed out",
        })
      }
    }, locationTimeoutMs)
  })
}
